import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { RemindersPanel } from "./RemindersPanel";
import { SystemStatusBadge } from "./SystemStatusBadge";
import { Mail, ClipboardCheck, ChevronRight, Newspaper, ExternalLink, Bell, PhoneCall, Sun, PoundSterling, CalendarCheck, Clock, FileText } from "lucide-react";

export function DashboardTab({ counts, bookings, isAdminLevel, isOfficeLevel, setTab, setClientsFilter, loadCounts, setOpenRef, openBooking, onOpenQuote }) {
  const open = openBooking || ((ref) => setOpenRef(ref));
  const unpaid = bookings.filter(b => b.status === "pending_payment").length;
  const [remindersFilter, setRemindersFilter] = useState("open");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const jumpToOverdue = () => {
    setRemindersFilter("open"); setOverdueOnly(true);
    setTimeout(() => {
      const el = document.getElementById("dashboard-reminders");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="dashboard-digest">
        <button onClick={() => { setTab("messages"); setTimeout(loadCounts, 1200); }} className="border border-[#E5E7EB] bg-white p-4 text-left hover:bg-[#FBF3EC]">
          <div className="label-eyebrow">Unread messages</div>
          <div className="text-3xl font-black" data-testid="digest-unread">{counts.messages_unread}</div>
        </button>
        <button data-testid="digest-overdue-tile" onClick={jumpToOverdue}
          className={`border bg-white p-4 text-left hover:bg-[#FBF3EC] transition-colors ${counts.overdue_reminders > 0 ? "border-[#C0392B]" : "border-[#E5E7EB]"}`}>
          <div className="label-eyebrow">Overdue / due reminders</div>
          <div className={`text-3xl font-black ${counts.overdue_reminders > 0 ? "text-[#C0392B]" : ""}`} data-testid="digest-overdue">{counts.overdue_reminders}</div>
          <div className="text-[11px] text-[#6B7280] mt-0.5">{counts.overdue_reminders > 0 ? "Click to jump to overdue list" : "Nothing overdue"}</div>
        </button>
        <button data-testid="digest-unpaid-tile" onClick={() => { setTab("clients"); setClientsFilter("unpaid"); }}
          className={`border bg-white p-4 text-left hover:bg-[#FBF3EC] transition-colors ${unpaid > 0 ? "border-[#C79A3B]" : "border-[#E5E7EB]"}`}>
          <div className="label-eyebrow">Unpaid bookings</div>
          <div className={`text-3xl font-black ${unpaid > 0 ? "text-[#C79A3B]" : ""}`} data-testid="digest-unpaid">{unpaid}</div>
          <div className="text-[11px] text-[#6B7280] mt-0.5">{unpaid > 0 ? "Deposit not yet paid — click to chase" : "All deposits paid"}</div>
        </button>
        <button data-testid="digest-enquiries-tile" onClick={() => setTab("enquiries")}
          className={`border bg-white p-4 text-left hover:bg-[#FBF3EC] transition-colors ${counts.new_enquiries > 0 ? "border-[#C79A3B]" : "border-[#E5E7EB]"}`}>
          <div className="label-eyebrow">New enquiries</div>
          <div className={`text-3xl font-black ${counts.new_enquiries > 0 ? "text-[#C79A3B]" : ""}`} data-testid="digest-enquiries">{counts.new_enquiries}</div>
          <div className="text-[11px] text-[#6B7280] mt-0.5">{counts.new_enquiries > 0 ? "Incl. leads Daisy collected — click to action" : "All caught up"}</div>
        </button>
        {isOfficeLevel && (
          <button data-testid="digest-new-quotes-tile" onClick={() => { setTab("clients"); setClientsFilter("prospect"); }}
            className={`border bg-white p-4 text-left hover:bg-[#FBF3EC] transition-colors ${(counts.new_saved_quotes || 0) > 0 ? "border-[#C79A3B]" : "border-[#E5E7EB]"}`}>
            <div className="label-eyebrow">New quotes</div>
            <div className={`text-3xl font-black ${(counts.new_saved_quotes || 0) > 0 ? "text-[#C79A3B]" : ""}`} data-testid="digest-new-quotes">{counts.new_saved_quotes || 0}</div>
            <div className="text-[11px] text-[#6B7280] mt-0.5">{(counts.new_saved_quotes || 0) > 0 ? "Customers who just configured a quote — chase to convert to a booking" : "No new quotes right now"}</div>
          </button>
        )}
        {isOfficeLevel && ((counts.callbacks_pending || 0) > 0 || (counts.callbacks_overdue || 0) > 0) && (
          <button data-testid="digest-callbacks-tile" onClick={() => setTab("enquiries")}
            className={`border bg-white p-4 text-left hover:bg-[#FBF3EC] transition-colors ${(counts.callbacks_overdue || 0) > 0 ? "border-[#C0392B]" : (counts.callbacks_pending || 0) > 0 ? "border-[#C79A3B]" : "border-[#E5E7EB]"}`}>
            <div className="label-eyebrow flex items-center gap-1.5">
              Callbacks due
              {(counts.callbacks_overdue || 0) > 0 && (
                <span data-testid="digest-callbacks-overdue-badge" className="inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#C0392B] text-white rounded-sm">
                  {counts.callbacks_overdue} overdue
                </span>
              )}
            </div>
            <div className={`text-3xl font-black ${(counts.callbacks_overdue || 0) > 0 ? "text-[#C0392B]" : "text-[#C79A3B]"}`} data-testid="digest-callbacks">{counts.callbacks_pending || 0}</div>
            <div className="text-[11px] text-[#6B7280] mt-0.5">{(counts.callbacks_overdue || 0) > 0 ? "Chase these before the warm lead goes cold" : "Customers on the price page asked us to call"}</div>
          </button>
        )}
        {isAdminLevel && (
          <div className="border border-[#E5E7EB] bg-white p-4 flex flex-col justify-between">
            <div className="label-eyebrow">Team reports</div>
            <p className="text-xs text-[#6B7280] mb-2">Daily digest auto-emails at 08:00 · weekly summary lands Monday 08:15.</p>
            <div className="flex gap-2">
              <button data-testid="digest-send-now" onClick={async () => {
                try { const r = await api.post("/admin/digest/send"); toast.success(r.data.sent > 0 ? `Digest sent to ${r.data.sent}` : "Digest generated (email sending not configured — logged only)"); }
                catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
              }} className="de-btn-secondary justify-center text-xs flex-1">Daily digest</button>
              <button data-testid="weekly-summary-send-now" onClick={async () => {
                try { const r = await api.post("/admin/weekly-summary/send"); toast.success(r.data.sent > 0 ? `Weekly summary sent to ${r.data.sent}` : "Summary generated (email not configured — logged only)"); }
                catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
              }} className="de-btn-secondary justify-center text-xs flex-1">Weekly summary</button>
            </div>
          </div>
        )}
        {isOfficeLevel && <SystemStatusBadge isAdmin={isAdminLevel} />}
      </div>
      <TodayPanel isOfficeLevel={isOfficeLevel} openBooking={open} onOpenQuote={onOpenQuote} setTab={setTab} />
      <NeedsAttention open={open} />
      <MostAddedExtras />
      <LatestBlogPosts />
      <h2 id="dashboard-reminders" className="text-xl font-bold tracking-tight mb-4">Reminders &amp; workload</h2>
      <RemindersPanel onOpenBooking={(ref) => setOpenRef(ref)} onOpenQuote={onOpenQuote}
        filter={remindersFilter} setFilter={setRemindersFilter}
        overdueOnly={overdueOnly} setOverdueOnly={setOverdueOnly} />
    </div>
  );
}

const ACTION_ICONS = {
  chase_fee: [PoundSterling, "text-[#C0392B]"],
  complete_survey: [ClipboardCheck, "text-[#C0392B]"],
  book_survey: [CalendarCheck, "text-[#C79A3B]"],
  new_quote: [FileText, "text-[#C79A3B]"],
  quote_expiring: [Clock, "text-[#B45309]"],
};

function TodayPanel({ isOfficeLevel, openBooking, onOpenQuote, setTab }) {
  const [reminders, setReminders] = useState([]);
  const [unreadEmails, setUnreadEmails] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [actions, setActions] = useState([]);
  useEffect(() => {
    api.get("/admin/reminders?status=open").then(r => setReminders(r.data.items || [])).catch(() => {});
    api.get("/admin/needs-attention").then(r => setUnreadEmails(r.data.unread_emails || [])).catch(() => {});
    if (isOfficeLevel) {
      api.get("/admin/action-feed").then(r => setActions(r.data.items || [])).catch(() => {});
      api.get("/admin/enquiries").then(r =>
        setCallbacks((r.data.items || []).filter(e => e.type === "callback_request" && e.status === "new"))
      ).catch(() => {});
    }
  }, [isOfficeLevel]);

  const today = new Date().toISOString().slice(0, 10);
  const due = reminders.filter(r => r.due_date && r.due_date <= today);
  const total = due.length + unreadEmails.length + callbacks.length + actions.length;

  return (
    <div className="border border-[#E5E7EB] bg-white p-5 mb-8" data-testid="today-panel">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2"><Sun size={18} className="text-[#C79A3B]"/> Today</h2>
        <span className="text-xs text-[#6B7280]">{total} thing{total === 1 ? "" : "s"} to action</span>
      </div>
      <p className="text-xs text-[#6B7280] mb-4">Your morning to-do list — fees to chase, surveys to book, expiring quotes, due reminders, callbacks and client replies, in one place.</p>
      {total === 0 ? (
        <div className="text-sm text-[#6B7280] py-3" data-testid="today-empty">Nothing due — you&apos;re all caught up. ☀️</div>
      ) : (
        <ul className="space-y-1.5">
          {actions.map(a => {
            const [Icon, color] = ACTION_ICONS[a.kind] || [Bell, "text-[#C79A3B]"];
            return (
              <li key={`act-${a.kind}-${a.reference || a.quote_id}`}>
                <button data-testid={`today-action-${a.kind}-${a.reference || a.quote_id}`}
                  onClick={() => a.quote_id ? (onOpenQuote && onOpenQuote(a.quote_id)) : openBooking(a.reference, a.tab)}
                  className="w-full flex items-center gap-2.5 border border-[#E5E7EB] px-3 py-2 text-sm text-left hover:bg-[#FBF3EC]">
                  <Icon size={14} className={`${color} shrink-0`} />
                  <span className="font-bold text-[#12263A] truncate">{a.label}</span>
                  <span className="text-xs text-[#6B7280] truncate">{a.who}</span>
                  <span className="ml-auto text-[11px] font-bold text-[#C79A3B] shrink-0">{a.reference || "quote"}</span>
                </button>
              </li>
            );
          })}
          {due.map(r => (
            <li key={`rem-${r.id}`}>
              <button data-testid={`today-reminder-${r.id}`}
                onClick={() => (r.quote_id && onOpenQuote) ? onOpenQuote(r.quote_id) : r.reference ? openBooking(r.reference) : null}
                className="w-full flex items-center gap-2.5 border border-[#E5E7EB] px-3 py-2 text-sm text-left hover:bg-[#FBF3EC]">
                <Bell size={14} className={r.due_date < today ? "text-[#C0392B] shrink-0" : "text-[#C79A3B] shrink-0"} />
                <span className="font-bold text-[#12263A] truncate">{r.title}</span>
                <span className="text-xs text-[#6B7280] truncate">{r.client_name || r.reference || ""}</span>
                <span className={`ml-auto text-[11px] font-bold shrink-0 ${r.due_date < today ? "text-[#C0392B]" : "text-[#6B7280]"}`}>
                  {r.due_date < today ? "overdue" : "due today"}
                </span>
              </button>
            </li>
          ))}
          {callbacks.map(c => (
            <li key={`cb-${c.id}`}>
              <button data-testid={`today-callback-${c.id}`} onClick={() => setTab("enquiries")}
                className="w-full flex items-center gap-2.5 border border-[#E5E7EB] px-3 py-2 text-sm text-left hover:bg-[#FBF3EC]">
                <PhoneCall size={14} className="text-[#C79A3B] shrink-0" />
                <span className="font-bold text-[#12263A] truncate">Call back {c.name || c.email || "a client"}</span>
                <span className="text-xs text-[#6B7280] truncate">{c.phone || ""}</span>
                <span className="ml-auto text-[11px] font-bold text-[#6B7280] shrink-0">callback</span>
              </button>
            </li>
          ))}
          {unreadEmails.map(u => (
            <li key={`em-${u.reference}`}>
              <button data-testid={`today-email-${u.reference}`} onClick={() => openBooking(u.reference, "emails")}
                className="w-full flex items-center gap-2.5 border border-[#E5E7EB] px-3 py-2 text-sm text-left hover:bg-[#FBF3EC]">
                <Mail size={14} className="text-[#2E6BB0] shrink-0" />
                <span className="font-bold text-[#12263A] truncate">Reply to {u.name}</span>
                <span className="ml-auto bg-[#C0392B] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0">{u.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NeedsAttention({ open }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/needs-attention").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const unread = data.unread_emails || [];
  const awaiting = data.awaiting_confirmation || [];
  const nothing = unread.length === 0 && awaiting.length === 0;
  return (
    <div className="border border-[#E5E7EB] bg-white p-5 mb-8" data-testid="needs-attention">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-xl font-bold tracking-tight">Needs attention</h2>
        <span className="text-xs text-[#6B7280]">{data.counts.unread} unread · {data.counts.awaiting} to confirm</span>
      </div>
      <p className="text-xs text-[#6B7280] mb-4">Client replies and jobs waiting on survey confirmation — action them before they slip.</p>
      {nothing ? (
        <div className="text-sm text-[#6B7280] py-3" data-testid="needs-attention-empty">Nothing outstanding &mdash; you&apos;re all caught up.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Mail size={13} /> Unread client emails</div>
            {unread.length === 0 ? <div className="text-sm text-[#9CA3AF]">No unread client emails.</div> : (
              <ul className="space-y-1.5">
                {unread.map((u) => (
                  <li key={u.reference}>
                    <button data-testid={`attention-email-${u.reference}`} onClick={() => open(u.reference, "emails")}
                      className="w-full flex items-center justify-between gap-2 border border-[#E5E7EB] px-3 py-2 text-sm text-left hover:bg-[#FBF3EC]">
                      <span className="font-bold text-[#12263A] truncate">{u.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="bg-[#C0392B] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{u.count}</span>
                        <ChevronRight size={14} className="text-[#9CA3AF]" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><ClipboardCheck size={13} /> Awaiting job confirmation</div>
            {awaiting.length === 0 ? <div className="text-sm text-[#9CA3AF]">No jobs awaiting confirmation.</div> : (
              <ul className="space-y-1.5">
                {awaiting.map((a) => (
                  <li key={a.reference}>
                    <button data-testid={`attention-confirm-${a.reference}`} onClick={() => open(a.reference, "job")}
                      className="w-full flex items-center justify-between gap-2 border border-[#E5E7EB] px-3 py-2 text-sm text-left hover:bg-[#FBF3EC]">
                      <span className="font-bold text-[#12263A] truncate">{a.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] uppercase tracking-wider text-[#C79A3B] font-bold">{a.reference}</span>
                        <ChevronRight size={14} className="text-[#9CA3AF]" />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LatestBlogPosts() {
  const [state, setState] = useState({ items: [], loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    api.get("/public/blog/articles").then(r => {
      if (cancelled) return;
      setState({ items: (r.data.items || []).slice(0, 3), loading: false, error: r.data.error || null });
    }).catch(e => {
      if (!cancelled) setState({ items: [], loading: false, error: e?.response?.data?.detail || "Could not load" });
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="border border-[#E5E7EB] bg-white p-5 mb-8" data-testid="dashboard-latest-blog">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Newspaper size={18} className="text-[#C79A3B]"/> Latest blog posts
        </h2>
        <a href="/blog" target="_blank" rel="noreferrer" className="text-xs de-link inline-flex items-center gap-1" data-testid="dashboard-blog-view-all">
          View on site <ExternalLink size={11}/>
        </a>
      </div>
      <p className="text-xs text-[#6B7280] mb-4">Auto-refreshed daily at 05:00 UTC from BabyLoveGrowth &mdash; a quick check that today&apos;s SEO content shipped.</p>
      {state.loading && <div className="text-sm text-[#6B7280]" data-testid="dashboard-blog-loading">Loading&hellip;</div>}
      {!state.loading && state.items.length === 0 && (
        <div className="text-sm text-[#6B7280]" data-testid="dashboard-blog-empty">
          {state.error ? `No cached posts (${state.error})` : "No posts yet — publish one on BabyLoveGrowth."}
        </div>
      )}
      {!state.loading && state.items.length > 0 && (
        <ul className="divide-y divide-[#F0EAE0]">
          {state.items.map(p => (
            <li key={p.id} data-testid={`dashboard-blog-row-${p.id}`}>
              <a href={`/blog/${p.id}`} target="_blank" rel="noreferrer" className="flex items-start justify-between gap-3 py-2.5 hover:bg-[#FBF3EC] -mx-2 px-2 transition-colors group">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#12263A] truncate group-hover:text-[#C79A3B]">{p.title}</div>
                  {p.published_at && (
                    <div className="text-[11px] text-[#6B7280] mt-0.5">{new Date(p.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                  )}
                </div>
                <ChevronRight size={16} className="text-[#6B7280] group-hover:text-[#C79A3B] flex-shrink-0 mt-1"/>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function MostAddedExtras() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/insights/extras").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const top = (data.extras || []).slice(0, 8);
  const max = top.length ? top[0].count : 0;
  return (
    <div className="border border-[#E5E7EB] bg-white p-5 mb-8" data-testid="dashboard-extras">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="text-xl font-bold tracking-tight">Most-added extras</h2>
        <span className="text-xs text-[#6B7280]">across {data.total_designs} saved design{data.total_designs === 1 ? "" : "s"}</span>
      </div>
      <p className="text-xs text-[#6B7280] mb-4">Which optional configurator items clients add most &mdash; a quick read on what&apos;s driving revenue.</p>
      {top.length === 0 ? (
        <div className="text-sm text-[#6B7280] py-4" data-testid="extras-empty">No saved designs yet — insights appear once clients start configuring quotes.</div>
      ) : (
        <ul className="space-y-2.5" data-testid="extras-list">
          {top.map((e) => (
            <li key={e.key} data-testid={`extra-row-${e.key}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-[#12263A]">{e.label}</span>
                <span className="text-[#6B7280] tabular-nums">{e.count} · {e.pct}%</span>
              </div>
              <div className="h-2 bg-[#F1EEE8] rounded-full overflow-hidden">
                <div className="h-full bg-[#C79A3B] rounded-full" style={{ width: `${max ? Math.round(e.count / max * 100) : 0}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
